import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';
import { Loader2, UserPlus, Mail, Phone, Building2, MapPin, FileText, ChevronRight } from 'lucide-react';

interface SignUpFormData {
  firstName: string;
  lastName: string;
  company: string;
  email: string;
  phone: string;
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  npiNumber: string;
  deaNumber: string;
}

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
  'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
  'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
  'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
  'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
];

const SignUpForm = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  
  const form = useForm<SignUpFormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      company: '',
      email: '',
      phone: '',
      streetAddress: '',
      city: '',
      state: '',
      zipCode: '',
      npiNumber: '',
      deaNumber: '',
    },
  });

  const onSubmit = async (data: SignUpFormData) => {
    setIsLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);
      toast({
        title: "Registration Started",
        description: "Please review and accept our terms to complete your registration.",
      });
      // Navigate to onboarding wizard to start the flow
      navigate('/onboarding?step=tos');
    }, 2000);
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Personal Information Card */}
          <Card className="border-0 shadow-lg border-l-4 border-l-emerald-500">
            <CardHeader className="bg-gradient-to-r from-vitl-green/5 to-vitl-green/10 border-b">
              <CardTitle className="flex items-center gap-2 text-vitl-navy">
                <UserPlus className="h-5 w-5 text-vitl-green" />
                Personal Information
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="firstName"
                  rules={{ 
                    required: 'First name is required',
                    pattern: {
                      value: /^[a-zA-Z\s]+$/,
                      message: 'First name can only contain letters'
                    }
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-vitl-navy font-medium">First Name</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="John" 
                          className="border-vitl-dark/20 focus:border-vitl-green focus:ring-vitl-green"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="lastName"
                  rules={{ 
                    required: 'Last name is required',
                    pattern: {
                      value: /^[a-zA-Z\s]+$/,
                      message: 'Last name can only contain letters'
                    }
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-vitl-navy font-medium">Last Name</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Doe" 
                          className="border-vitl-dark/20 focus:border-vitl-green focus:ring-vitl-green"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          {/* Contact Information Card */}
          <Card className="border-0 shadow-lg border-l-4 border-l-emerald-500">
            <CardHeader className="bg-gradient-to-r from-vitl-green/5 to-vitl-green/10 border-b">
              <CardTitle className="flex items-center gap-2 text-vitl-navy">
                <Mail className="h-5 w-5 text-vitl-green" />
                Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="email"
                  rules={{
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-vitl-navy font-medium">Email Address</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Mail className="absolute left-3 top-2.5 h-4 w-4 text-vitl-dark/40" />
                          <Input 
                            type="email" 
                            placeholder="john.doe@clinic.com" 
                            className="pl-10 border-vitl-dark/20 focus:border-vitl-green focus:ring-vitl-green"
                            {...field} 
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone"
                  rules={{ 
                    required: 'Phone number is required',
                    pattern: {
                      value: /^\d{10}$/,
                      message: 'Phone number must be exactly 10 digits'
                    }
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-vitl-navy font-medium">Phone Number</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Phone className="absolute left-3 top-2.5 h-4 w-4 text-vitl-dark/40" />
                          <Input 
                            type="tel" 
                            placeholder="5551234567" 
                            className="pl-10 border-vitl-dark/20 focus:border-vitl-green focus:ring-vitl-green"
                            maxLength={10}
                            {...field}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          {/* Address Information Card */}
          <Card className="border-0 shadow-lg border-l-4 border-l-emerald-500">
            <CardHeader className="bg-gradient-to-r from-vitl-green/5 to-vitl-green/10 border-b">
              <CardTitle className="flex items-center gap-2 text-vitl-navy">
                <MapPin className="h-5 w-5 text-vitl-green" />
                Address Information
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-6">
                <FormField
                  control={form.control}
                  name="streetAddress"
                  rules={{ required: 'Street address is required' }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-vitl-navy font-medium">Street Address</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="123 Medical Center Dr" 
                          className="border-vitl-dark/20 focus:border-vitl-green focus:ring-vitl-green"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <FormField
                    control={form.control}
                    name="city"
                    rules={{ 
                      required: 'City is required',
                      pattern: {
                        value: /^[a-zA-Z\s]+$/,
                        message: 'City can only contain letters'
                      }
                    }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-vitl-navy font-medium">City</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="New York" 
                            className="border-vitl-dark/20 focus:border-vitl-green focus:ring-vitl-green"
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="state"
                    rules={{ required: 'State is required' }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-vitl-navy font-medium">State</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger className="border-vitl-dark/20 focus:border-vitl-green focus:ring-vitl-green">
                              <SelectValue placeholder="Select state" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {US_STATES.map((state) => (
                              <SelectItem key={state} value={state}>
                                {state}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="zipCode"
                    rules={{ 
                      required: 'ZIP code is required',
                      pattern: {
                        value: /^\d{5}$/,
                        message: 'ZIP code must be exactly 5 digits'
                      }
                    }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-vitl-navy font-medium">ZIP Code</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="10001" 
                            className="border-vitl-dark/20 focus:border-vitl-green focus:ring-vitl-green"
                            maxLength={5}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Professional Information Card */}
          <Card className="border-0 shadow-lg border-l-4 border-l-emerald-500">
            <CardHeader className="bg-gradient-to-r from-vitl-green/5 to-vitl-green/10 border-b">
              <CardTitle className="flex items-center gap-2 text-vitl-navy">
                <Building2 className="h-5 w-5 text-vitl-green" />
                Professional Information
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-6">
                <FormField
                  control={form.control}
                  name="company"
                  rules={{ required: 'Company name is required' }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-vitl-navy font-medium">Company / Clinic Name</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Building2 className="absolute left-3 top-2.5 h-4 w-4 text-vitl-dark/40" />
                          <Input 
                            placeholder="Medical Center Name" 
                            className="pl-10 border-vitl-dark/20 focus:border-vitl-green focus:ring-vitl-green"
                            {...field} 
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="npiNumber"
                    rules={{ 
                      required: 'NPI Number is required',
                      pattern: {
                        value: /^\d+$/,
                        message: 'NPI Number can only contain numbers'
                      }
                    }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-vitl-navy font-medium">NPI Number</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <FileText className="absolute left-3 top-2.5 h-4 w-4 text-vitl-dark/40" />
                            <Input 
                              placeholder="1234567890" 
                              className="pl-10 border-vitl-dark/20 focus:border-vitl-green focus:ring-vitl-green"
                              {...field}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="deaNumber"
                    rules={{ 
                      required: 'DEA Number is required'
                    }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-vitl-navy font-medium">DEA Number</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <FileText className="absolute left-3 top-2.5 h-4 w-4 text-vitl-dark/40" />
                            <Input 
                              placeholder="AB1234567" 
                              className="pl-10 border-vitl-dark/20 focus:border-vitl-green focus:ring-vitl-green"
                              {...field}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-col gap-4">
            <Button 
              type="submit" 
              className="w-full bg-vitl-green hover:bg-vitl-green/90 text-white font-semibold py-3 text-base"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating Your Account...
                </>
              ) : (
                <>
                  Complete Registration
                  <ChevronRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
            
            <p className="text-center text-sm text-vitl-dark/70">
              Already have an account?{' '}
              <Link to="/" className="text-vitl-green hover:text-vitl-green/90 font-medium underline">
                Sign in here
              </Link>
            </p>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default SignUpForm;